import { useState, useRef } from "react";
import { Download, Upload, X, Edit3, Check, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const EditableText = ({ value, onChange, multiline = false, className = "", style = {} }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || "");

  if (editing) {
    const Component = multiline ? "textarea" : "input";
    return (
      <span className="inline-flex items-start gap-1" style={style}>
        <Component
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={`flex-1 bg-white border border-amber-400/50 rounded px-2 py-1 text-gray-800 text-sm focus:border-amber-500 outline-none ${multiline ? "min-h-[60px] resize-y" : ""} ${className}`}
          autoFocus
          onKeyDown={(e) => { if (e.key === "Enter" && !multiline) { onChange(text); setEditing(false); } }}
        />
        <button onClick={() => { onChange(text); setEditing(false); }} className="p-1 text-emerald-600 hover:text-emerald-500 shrink-0"><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => { setText(value || ""); setEditing(false); }} className="p-1 text-red-400 hover:text-red-500 shrink-0"><X className="w-3.5 h-3.5" /></button>
      </span>
    );
  }

  return (
    <span onClick={() => setEditing(true)} className={`cursor-pointer hover:bg-amber-50 rounded px-0.5 transition-colors group ${className}`} title="Click to edit" style={style}>
      {value || "[Click to add]"}
      <Edit3 className="w-3 h-3 ml-1 inline opacity-0 group-hover:opacity-40 transition-opacity" />
    </span>
  );
};

const ReportPreview = ({ report, onClose, onDownloadDocx, onUploadPdf, onUpdateContent, token, uploadingPdf }) => {
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const c = report?.content || {};
  const s = c.sections || {};
  const snap = c.assessment_snapshot || {};

  const updateSection = (path, value) => {
    const newContent = JSON.parse(JSON.stringify(c));
    const keys = path.split(".");
    let obj = newContent;
    for (let i = 0; i < keys.length - 1; i++) {
      if (keys[i].match(/^\d+$/)) obj = obj[parseInt(keys[i])];
      else obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onUpdateContent(newContent);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const el = printRef.current;
      if (!el) { toast.error("Print area not found"); return; }

      // Temporarily expand the container so html2pdf captures ALL content
      const prevMaxH = el.style.maxHeight;
      const prevOverflow = el.style.overflow;
      el.style.maxHeight = "none";
      el.style.overflow = "visible";

      const opt = {
        margin: [8, 10, 8, 10],
        filename: `3A_Report_${c.client_name || "client"}_${c.assessment_item || "report"}.pdf`.replace(/\s/g, "_"),
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#FFFDF7", scrollY: 0 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"], avoid: ["tr", "div[style]"] },
      };
      await html2pdf().set(opt).from(el).save();

      // Restore scroll constraint
      el.style.maxHeight = prevMaxH;
      el.style.overflow = prevOverflow;

      toast.success("PDF exported successfully");
    } catch (err) {
      // Restore scroll on error too
      if (printRef.current) {
        printRef.current.style.maxHeight = "";
        printRef.current.style.overflow = "";
      }
      toast.error("PDF export failed: " + err.message);
    }
    setExporting(false);
  };

  if (!report) return null;

  const COLORS = {
    bg: "#FFFDF7", headerBg: "#1a1025", gold: "#B8860B", goldLight: "#D4A843",
    a1: "#C67D20", a2: "#2E7D32", a3: "#5B2D8E",
    text: "#2C2417", textLight: "#5C4D3C", textMuted: "#8A7A65",
    border: "#E8DCC8", borderLight: "#F0E8D8", accent: "#F5EFE0",
    scriptureText: "#6B4C8A",
  };

  return (
    <div className="space-y-4" data-testid="report-preview-section">
      {/* Action Bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading text-lg text-divine-gold flex items-center gap-2">
          <FileText className="w-5 h-5" /> REPORT PREVIEW & EDITOR
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={handleExportPdf} disabled={exporting}
            className="bg-emerald-600 text-white hover:bg-emerald-700 text-xs" data-testid="export-pdf-btn">
            <Download className="w-3 h-3 mr-1" />{exporting ? "Exporting..." : "Export as PDF"}
          </Button>
          <Button size="sm" variant="outline" className="border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 text-xs"
            onClick={() => onDownloadDocx(report.report_id)} data-testid="preview-download-docx">
            <Download className="w-3 h-3 mr-1" />Download .docx
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf" className="hidden"
              onChange={(e) => e.target.files[0] && onUploadPdf(report.report_id, e.target.files[0])} />
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
              <Upload className="w-3 h-3" />{uploadingPdf ? "Uploading..." : "Upload PDF"}
            </span>
          </label>
          <Button size="sm" variant="ghost" className="text-chakra-crown/50 hover:text-divine-gold text-xs"
            onClick={onClose} data-testid="close-preview-btn">
            <X className="w-4 h-4" /> Back
          </Button>
        </div>
      </div>
      <p className="text-chakra-crown/40 text-xs">Click any text to edit inline. Use "Export as PDF" for a print-ready professional document.</p>

      {/* ===== PRINTABLE REPORT — LIGHT PROFESSIONAL THEME ===== */}
      <div ref={printRef} className="rounded-xl overflow-hidden border border-divine-gold/20 max-h-[70vh] overflow-y-auto"
        style={{ background: COLORS.bg, color: COLORS.text, fontFamily: "'Georgia', 'Times New Roman', serif" }}
        data-testid="report-preview-content">

        {/* Cover Header — Dark band */}
        <div style={{ background: COLORS.headerBg, padding: "28px 40px", textAlign: "center" }}>
          <div style={{ fontSize: "26px", fontWeight: "bold", color: COLORS.goldLight, letterSpacing: "6px" }}>SIWON HARMONY</div>
          <div style={{ fontSize: "14px", color: "#a08860", marginTop: "4px", letterSpacing: "3px" }}>3A FRAMEWORK REPORT</div>
          <div style={{ marginTop: "16px", display: "flex", justifyContent: "center", gap: "32px", flexWrap: "wrap" }}>
            {[["Client", c.client_name], ["Coach", c.coach_name], ["Module", c.module]].map(([label, val]) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "9px", color: COLORS.goldLight, letterSpacing: "2px", textTransform: "uppercase" }}>{label}</div>
                <div style={{ fontSize: "14px", color: "#e8d5b0", marginTop: "2px" }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "10px", fontSize: "11px", color: "#8a7a65" }}>{c.assessment_type} — {c.assessment_item} | {c.date}</div>
        </div>

        {/* Assessment Snapshot */}
        {c.score_summary && c.score_summary !== "No assessment data available yet" && (
          <div style={{ padding: "20px 40px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: "11px", color: COLORS.gold, letterSpacing: "2px", fontWeight: "bold", marginBottom: "12px" }}>CLIENT ASSESSMENT SNAPSHOT</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {/* Vikara — Relationship only */}
              {snap.vikara && Object.keys(snap.vikara).filter(k => k !== "dominant").length > 0 && (
                <div style={{ background: COLORS.accent, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                  <div style={{ fontSize: "9px", color: COLORS.gold, fontWeight: "bold", marginBottom: "5px", letterSpacing: "1px" }}>VIKARA PROFILE</div>
                  {Object.entries(snap.vikara).filter(([k]) => k !== "dominant").map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight, padding: "1px 0" }}>
                      <span style={{ textTransform: "capitalize" }}>{k}</span>
                      <span style={{ color: COLORS.gold, fontWeight: "600" }}>{typeof v === "number" ? `${v.toFixed(1)}/10` : v}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Guna — Relationship only (now always in percentage from backend) */}
              {snap.guna && Object.keys(snap.guna).filter(k => k !== "dominant").length > 0 && (
                <div style={{ background: COLORS.accent, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                  <div style={{ fontSize: "9px", color: COLORS.gold, fontWeight: "bold", marginBottom: "5px", letterSpacing: "1px" }}>GUNA DISTRIBUTION</div>
                  {Object.entries(snap.guna).filter(([k]) => k !== "dominant").map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight, padding: "1px 0" }}>
                      <span style={{ textTransform: "capitalize" }}>{k}</span>
                      <span style={{ color: COLORS.gold, fontWeight: "600" }}>{typeof v === "number" ? `${v.toFixed(1)}%` : v}</span>
                    </div>
                  ))}
                  {snap.guna.dominant && <div style={{ fontSize: "9px", color: COLORS.textMuted, marginTop: "3px" }}>Dominant: {snap.guna.dominant}</div>}
                </div>
              )}
              {/* Energy Polarity — Relationship only */}
              {snap.energy && (snap.energy.masculine || snap.energy.feminine) && (
                <div style={{ background: COLORS.accent, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                  <div style={{ fontSize: "9px", color: COLORS.gold, fontWeight: "bold", marginBottom: "5px", letterSpacing: "1px" }}>ENERGY POLARITY</div>
                  {[["Masculine", snap.energy.masculine], ["Feminine", snap.energy.feminine], ["Harmony", snap.energy.harmony_potential]].filter(([, v]) => v !== undefined).map(([label, v]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight, padding: "1px 0" }}>
                      <span>{label}</span><span style={{ color: COLORS.gold, fontWeight: "600" }}>{(v * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Life Diagnostic — Life only */}
              {snap.life_diagnostic && Object.keys(snap.life_diagnostic).length > 0 && (
                <div style={{ background: COLORS.accent, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                  <div style={{ fontSize: "9px", color: COLORS.gold, fontWeight: "bold", marginBottom: "5px", letterSpacing: "1px" }}>LIFE DIAGNOSTIC</div>
                  {Object.entries(snap.life_diagnostic).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight, padding: "1px 0" }}>
                      <span>Module {k}</span>
                      <span style={{ color: COLORS.gold, fontWeight: "600" }}>{typeof v === "object" ? v.score : v}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Kosha — Both modules */}
              {snap.kosha && Object.keys(snap.kosha).length > 0 && (
                <div style={{ background: COLORS.accent, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                  <div style={{ fontSize: "9px", color: COLORS.gold, fontWeight: "bold", marginBottom: "5px", letterSpacing: "1px" }}>KOSHA ASSESSMENT</div>
                  {Object.entries(snap.kosha).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight, padding: "1px 0" }}>
                      <span style={{ textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</span>
                      <span style={{ color: COLORS.gold, fontWeight: "600" }}>{typeof v === "object" ? `${v.score}%` : v}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Other Metrics — always shown if data exists */}
              {(snap.tatkalik_ids || snap.issue_count > 0 || snap.trinity_gap !== null && snap.trinity_gap !== undefined) && (
                <div style={{ background: COLORS.accent, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                  <div style={{ fontSize: "9px", color: COLORS.gold, fontWeight: "bold", marginBottom: "5px", letterSpacing: "1px" }}>OTHER METRICS</div>
                  {snap.trinity_gap !== null && snap.trinity_gap !== undefined && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight }}><span>Trinity Gap</span><span style={{ color: COLORS.gold, fontWeight: "600" }}>{snap.trinity_gap}</span></div>
                  )}
                  {snap.tatkalik_ids && <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight }}><span>Triggers</span><span style={{ color: COLORS.gold, fontWeight: "600" }}>{snap.tatkalik_ids.join(", ")}</span></div>}
                  {snap.issue_count > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textLight }}><span>Issues</span><span style={{ color: COLORS.gold, fontWeight: "600" }}>{snap.issue_count}</span></div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compassionate Note */}
        {s.compassionate_note && (
          <div style={{ padding: "20px 40px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: "11px", color: COLORS.gold, letterSpacing: "2px", fontWeight: "bold", marginBottom: "8px" }}>A COMPASSIONATE NOTE</div>
            <div style={{ fontSize: "12px", color: COLORS.textLight, lineHeight: "1.8", whiteSpace: "pre-line" }}>
              <EditableText value={s.compassionate_note} onChange={(v) => updateSection("sections.compassionate_note", v)} multiline />
            </div>
          </div>
        )}

        {/* A1 AWARENESS */}
        {s.a1_awareness && (
          <div style={{ padding: "20px 40px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: "15px", color: COLORS.a1, fontWeight: "bold", letterSpacing: "1px", marginBottom: "16px", paddingBottom: "8px", borderBottom: `2px solid ${COLORS.a1}` }}>
              <EditableText value={s.a1_awareness.title} onChange={(v) => updateSection("sections.a1_awareness.title", v)} />
            </div>
            {["root_cause", "chain_of_destruction", "seats_of_challenge", "turning_point"].map(key => {
              const sub = s.a1_awareness[key];
              if (!sub) return null;
              const textKey = ["diagnosis", "explanation", "analysis", "insight"].find(k => sub[k]);
              const displayTitle = key.replace(/_/g, " ").toUpperCase();
              return (
                <div key={key} style={{ marginBottom: "14px", padding: "12px 16px", background: "#FFF9EE", borderRadius: "6px", borderLeft: `3px solid ${COLORS.a1}` }}>
                  <div style={{ fontSize: "10px", color: COLORS.a1, fontWeight: "bold", letterSpacing: "1px", marginBottom: "6px" }}>
                    <EditableText value={sub._title || displayTitle} onChange={(v) => updateSection(`sections.a1_awareness.${key}._title`, v)} />
                  </div>
                  <div style={{ fontSize: "11px", color: COLORS.scriptureText, fontStyle: "italic", marginBottom: "4px" }}>
                    <EditableText value={sub.scripture_ref} onChange={(v) => updateSection(`sections.a1_awareness.${key}.scripture_ref`, v)} />
                  </div>
                  {sub.scripture_quote && (
                    <div style={{ fontSize: "11px", color: COLORS.textMuted, fontStyle: "italic", marginBottom: "8px", padding: "6px 12px", borderLeft: `2px solid ${COLORS.borderLight}`, background: "#FFFCF3" }}>
                      "<EditableText value={sub.scripture_quote} onChange={(v) => updateSection(`sections.a1_awareness.${key}.scripture_quote`, v)} multiline />"
                    </div>
                  )}
                  {textKey && (
                    <div style={{ fontSize: "12px", color: COLORS.textLight, lineHeight: "1.7" }}>
                      <EditableText value={sub[textKey]} onChange={(v) => updateSection(`sections.a1_awareness.${key}.${textKey}`, v)} multiline />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* A2 ALIGNMENT */}
        {s.a2_alignment && (
          <div style={{ padding: "20px 40px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: "15px", color: COLORS.a2, fontWeight: "bold", letterSpacing: "1px", marginBottom: "16px", paddingBottom: "8px", borderBottom: `2px solid ${COLORS.a2}` }}>
              <EditableText value={s.a2_alignment.title} onChange={(v) => updateSection("sections.a2_alignment.title", v)} />
            </div>
            {(s.a2_alignment.steps || []).map((step, idx) => {
              const textKey = ["guidance", "practices", "strategy", "encouragement"].find(k => step[k]);
              return (
                <div key={idx} style={{ marginBottom: "14px", padding: "12px 16px", background: "#F5FBF5", borderRadius: "6px", borderLeft: `3px solid ${COLORS.a2}` }}>
                  <div style={{ fontSize: "10px", color: COLORS.a2, fontWeight: "bold", letterSpacing: "1px", marginBottom: "6px" }}>
                    <EditableText value={step.title} onChange={(v) => updateSection(`sections.a2_alignment.steps.${idx}.title`, v)} />
                  </div>
                  <div style={{ fontSize: "11px", color: COLORS.scriptureText, fontStyle: "italic", marginBottom: "4px" }}>
                    <EditableText value={step.scripture_ref} onChange={(v) => updateSection(`sections.a2_alignment.steps.${idx}.scripture_ref`, v)} />
                  </div>
                  {step.scripture_quote && (
                    <div style={{ fontSize: "11px", color: COLORS.textMuted, fontStyle: "italic", marginBottom: "8px", padding: "6px 12px", borderLeft: `2px solid ${COLORS.borderLight}`, background: "#FCFFF8" }}>
                      "<EditableText value={step.scripture_quote} onChange={(v) => updateSection(`sections.a2_alignment.steps.${idx}.scripture_quote`, v)} multiline />"
                    </div>
                  )}
                  {textKey && (
                    <div style={{ fontSize: "12px", color: COLORS.textLight, lineHeight: "1.7" }}>
                      <EditableText value={step[textKey]} onChange={(v) => updateSection(`sections.a2_alignment.steps.${idx}.${textKey}`, v)} multiline />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* A3 AWAKENING */}
        {s.a3_awakening && (
          <div style={{ padding: "20px 40px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: "15px", color: COLORS.a3, fontWeight: "bold", letterSpacing: "1px", marginBottom: "16px", paddingBottom: "8px", borderBottom: `2px solid ${COLORS.a3}` }}>
              <EditableText value={s.a3_awakening.title} onChange={(v) => updateSection("sections.a3_awakening.title", v)} />
            </div>
            {(s.a3_awakening.wisdoms || []).map((w, idx) => {
              const textKey = ["insight", "recommendation", "path", "realization", "affirmation"].find(k => w[k]);
              return (
                <div key={idx} style={{ marginBottom: "14px", padding: "12px 16px", background: "#F8F4FD", borderRadius: "6px", borderLeft: `3px solid ${COLORS.a3}` }}>
                  <div style={{ fontSize: "10px", color: COLORS.a3, fontWeight: "bold", letterSpacing: "1px", marginBottom: "6px" }}>
                    <EditableText value={w.title} onChange={(v) => updateSection(`sections.a3_awakening.wisdoms.${idx}.title`, v)} />
                  </div>
                  <div style={{ fontSize: "11px", color: COLORS.scriptureText, fontStyle: "italic", marginBottom: "4px" }}>
                    <EditableText value={w.scripture_ref} onChange={(v) => updateSection(`sections.a3_awakening.wisdoms.${idx}.scripture_ref`, v)} />
                  </div>
                  {w.scripture_quote && (
                    <div style={{ fontSize: "11px", color: COLORS.textMuted, fontStyle: "italic", marginBottom: "8px", padding: "6px 12px", borderLeft: `2px solid ${COLORS.borderLight}`, background: "#FBF8FF" }}>
                      "<EditableText value={w.scripture_quote} onChange={(v) => updateSection(`sections.a3_awakening.wisdoms.${idx}.scripture_quote`, v)} multiline />"
                    </div>
                  )}
                  {textKey && (
                    <div style={{ fontSize: "12px", color: COLORS.textLight, lineHeight: "1.7" }}>
                      <EditableText value={w[textKey]} onChange={(v) => updateSection(`sections.a3_awakening.wisdoms.${idx}.${textKey}`, v)} multiline />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Summary Table — One verse per row, all cells editable */}
        {s.summary_table && s.summary_table.length > 0 && (
          <div style={{ padding: "20px 40px", borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: "15px", color: COLORS.gold, fontWeight: "bold", letterSpacing: "1px", textAlign: "center", marginBottom: "14px" }}>3A Framework at a Glance</div>
            <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${COLORS.border}` }}>
              <thead>
                <tr style={{ background: COLORS.headerBg }}>
                  <th style={{ padding: "8px 12px", fontSize: "10px", color: COLORS.goldLight, textAlign: "left", fontWeight: "bold", letterSpacing: "1px", width: "18%" }}>Stage</th>
                  <th style={{ padding: "8px 12px", fontSize: "10px", color: COLORS.goldLight, textAlign: "left", fontWeight: "bold", letterSpacing: "1px", width: "24%" }}>Scripture</th>
                  <th style={{ padding: "8px 12px", fontSize: "10px", color: COLORS.goldLight, textAlign: "left", fontWeight: "bold", letterSpacing: "1px", width: "58%" }}>Key Insight</th>
                </tr>
              </thead>
              <tbody>
                {s.summary_table.map((row, idx) => {
                  const stageColor = row.stage?.includes("Awareness") ? COLORS.a1 : row.stage?.includes("Alignment") ? COLORS.a2 : row.stage?.includes("Awakening") ? COLORS.a3 : "transparent";
                  const rowBg = idx % 2 === 0 ? "#FFFCF3" : COLORS.bg;
                  return (
                    <tr key={idx} style={{ background: rowBg, borderBottom: `1px solid ${COLORS.borderLight}` }}>
                      <td style={{ padding: "5px 12px", fontSize: "11px" }}>
                        {row.stage ? (
                          <span style={{ color: stageColor, fontWeight: "bold" }}>
                            <EditableText value={row.stage} onChange={(v) => updateSection(`sections.summary_table.${idx}.stage`, v)} />
                          </span>
                        ) : null}
                      </td>
                      <td style={{ padding: "5px 12px", fontSize: "11px", color: COLORS.scriptureText, fontStyle: "italic" }}>
                        <EditableText value={row.scripture} onChange={(v) => updateSection(`sections.summary_table.${idx}.scripture`, v)} />
                      </td>
                      <td style={{ padding: "5px 12px", fontSize: "11px", color: COLORS.textLight }}>
                        <EditableText value={row.key_insight} onChange={(v) => updateSection(`sections.summary_table.${idx}.key_insight`, v)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "14px 40px", textAlign: "center", background: COLORS.accent, borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: "9px", color: COLORS.textMuted, lineHeight: "1.5" }}>
            All wisdom sourced from vedabase.io — Bhagavad Gita As It Is & Srimad Bhagavatam by Srila Prabhupada
          </div>
          <div style={{ fontSize: "9px", color: COLORS.gold, marginTop: "3px", letterSpacing: "2px", fontWeight: "bold" }}>SIWON HARMONY</div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;
